import type { LessonCompletion } from '@orborix/shared-types';
import { PrIconCheck, PrIconChevR } from '../program/programIcons.js';

/**
 * The part «Próxima» opens, or `null`. Live from the confirmed credit on —
 * also while the coach still speaks the recognition (`closing`): the tap cuts
 * that audio, like «Terminar», since the part was saved before the card came.
 */
export function nextLessonTarget(part: LessonCompletion | null): string | null {
  if (!part?.nextSubUnitId) return null;
  return part.phase === 'closing' || part.phase === 'closed' ? part.nextSubUnitId : null;
}

/** The three ways out of a finished role-play (Emmanuel, 15/10). */
export interface ScenarioEndActions {
  /** «Ver mi cierre»: the debrief of this session. */
  onDebrief: () => void;
  /** «Repetir»: the same scene, a new session. */
  onRepeat: () => void;
  /** «Otro escenario»: the scenarios hub. */
  onOther: () => void;
}

function ScenarioEndButtons({ actions }: { actions: ScenarioEndActions }) {
  return (
    <div className="lesson-completed-actions">
      <button type="button" className="conn-btn conn-btn-primary" onClick={actions.onDebrief}>
        Ver mi cierre
      </button>
      <button type="button" className="conn-btn conn-btn-ghost" onClick={actions.onRepeat}>
        Repetir
      </button>
      <button type="button" className="conn-btn conn-btn-ghost" onClick={actions.onOther}>
        Otro escenario
      </button>
    </div>
  );
}

/**
 * Mission done with the scenario's bonus still pending: the role-play goes on,
 * this says what the bonus asks for and keeps the three ways out at hand.
 */
export function ScenarioBonusCard({
  bonusLabel,
  actions,
}: {
  bonusLabel: string;
  actions: ScenarioEndActions;
}) {
  return (
    <section className="lesson-completed-card slide-up" aria-label="Misión cumplida" aria-live="polite">
      <div className="lesson-completed-heading">
        <span className="lesson-completed-eyebrow">
          <PrIconCheck size={14} />
          Misión cumplida
        </span>
      </div>
      <p className="scenario-bonus-hint">
        <strong>Bonus:</strong> {bonusLabel}. Hacelo en la charla para lograrlo, o salí cuando quieras.
      </p>
      <ScenarioEndButtons actions={actions} />
    </section>
  );
}

export function LessonCompletedCard({
  completion: part,
  connected,
  onFinish,
  onProgram,
  onChoose,
  scenarioActions,
}: {
  completion: LessonCompletion;
  connected: boolean;
  onFinish: () => void;
  onProgram: () => void;
  onChoose: (choice: 'next' | 'chat') => void;
  /** Required to render a role-play's ending (`completion.scenario`). */
  scenarioActions?: ScenarioEndActions;
}) {
  if (part.scenario) {
    if (!scenarioActions) throw new Error('LessonCompletedCard: scenario ending without actions');
    return (
      <section className="lesson-completed-card slide-up" aria-label="Escenario completado" aria-live="polite">
        <div className="lesson-completed-heading">
          <span className="lesson-completed-eyebrow">
            <PrIconCheck size={14} />
            Escenario completado
          </span>
          <strong>{part.titleEs}</strong>
        </div>
        <ScenarioEndButtons actions={scenarioActions} />
        {part.scenario.bonusDone.length > 0 && (
          <small>Bonus logrado: {part.scenario.bonusDone.join(' · ')}</small>
        )}
      </section>
    );
  }
  // The card arrives with the server's confirmed credit, so it says
  // «completada» while the coach still speaks the recognition (encargo 62).
  const status = part.review
    ? 'Repaso completado'
    : part.unitComplete
      ? 'Unidad completada'
      : 'Clase completada';
  const deliveryFailed =
    part.phase === 'closed' &&
    part.closingOutcome != null &&
    part.closingOutcome !== 'delivered';

  return (
    <section
      className="lesson-completed-card slide-up"
      aria-label="Clase completada"
      aria-live="polite"
    >
      <div className="lesson-completed-heading">
        <span className="lesson-completed-eyebrow">
          <PrIconCheck size={14} />
          {status}
        </span>
        <strong>{part.titleEs}</strong>
      </div>
      {deliveryFailed && (
        <p>El cierre no pudo entregarse completo. Tu progreso está guardado.</p>
      )}
      <div className="lesson-completed-actions">
        <button
          type="button"
          className="conn-btn conn-btn-primary"
          onClick={onFinish}
          title="Salir de esta clase al Programa"
        >
          Terminar
        </button>
        {part.nextSubUnitId ? (
          <button
            type="button"
            className="conn-btn conn-btn-ghost"
            disabled={!connected || nextLessonTarget(part) === null}
            onClick={() => onChoose('next')}
            title={`${part.nextTitleEs ?? 'Próxima parte'} · inicia otra clase`}
          >
            Próxima<PrIconChevR size={15} />
          </button>
        ) : (
          <button
            type="button"
            className="conn-btn conn-btn-ghost"
            onClick={onProgram}
            title="Elegir tu próximo paso en el Programa"
          >
            Ver Programa
          </button>
        )}
      </div>
      <small>
        {part.nextSubUnitId
          ? `Próxima: ${part.nextTitleEs ?? 'siguiente parte'} · nueva clase`
          : 'Elegí tu próximo paso en el Programa'}
      </small>
    </section>
  );
}
